import React, { useState, useEffect } from 'react';
import { t } from '../utils/i18n';
import CommonModal from './Modal';


const WorkflowManagement = ({ api }) => {
  const workflowTypes = [
    { value: 'leave', label: 'Leave Management' },
    { value: 'payroll', label: 'Payroll' },
    { value: 'attendance', label: 'Attendance' },
  ];
  const [workflows, setWorkflows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [armyTypes, setArmyTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [viewingWorkflow, setViewingWorkflow] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    template_data: '',
    department_id: '',
    army_type_id: '',
    workflow_type: 'leave',
    priority: 0,
    nodes: [
      { node_type: 'start', configuration: {} },
      { node_type: 'approve', configuration: { required_role: '', step_name: '' } },
      { node_type: 'end', configuration: {} }
    ]
  });

  useEffect(() => {
    fetchWorkflows();
    fetchDepartments();
    fetchArmyTypes();
    fetchRoles();
  }, []);

  const fetchWorkflows = async (workflowType = '') => {
    try {
      const url = workflowType ? `/workflow_templates?workflow_type=${workflowType}` : '/workflow_templates';
      console.log('Fetching from URL:', url);
      const response = await api.get(url);
      setWorkflows(response.data.workflow_templates || []);
    } catch (error) {
      console.error('Error fetching workflows:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchArmyTypes = async () => {
    try {
      const response = await api.get('/army_types');
      setArmyTypes(response || []);
    } catch (error) {
      console.error('Error fetching army types:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleEdit = async (workflowId) => {
    try {
      const response = await api.get(`/workflow_templates/${workflowId}`);
      const workflow = response.data.workflow_template;
      
      // Parse configuration JSON strings to objects
      const parsedNodes = workflow.workflow_nodes?.map(node => ({
        ...node,
        configuration: typeof node.configuration === 'string' 
          ? JSON.parse(node.configuration || '{}') 
          : node.configuration || {}
      })) || [];
      
      setEditingWorkflow(workflow);
      setFormData({
        name: workflow.name,
        template_data: workflow.template_data,
        department_id: workflow.department_id || '',
        army_type_id: workflow.army_type_id || '',
        workflow_type: workflow.workflow_type || 'leave',
        priority: workflow.priority || 0,
        nodes: parsedNodes
      });
      setShowForm(true);
    } catch (error) {
      console.error('Error fetching workflow:', error);
    }
  };

  const handleView = async (workflowId) => {
    try {
      const response = await api.get(`/workflow_templates/${workflowId}`);
      setViewingWorkflow(response.data.workflow_template);
    } catch (error) {
      console.error('Error fetching workflow:', error);
    }
  };

  const handleDelete = async (workflowId) => {
    if (!confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      await api.delete(`/workflow_templates/${workflowId}`);
      alert('Workflow deleted successfully');
      fetchWorkflows();
    } catch (error) {
      console.error('Error deleting workflow:', error);
      alert('Failed to delete workflow');
    }
  };

  const addApprovalStep = () => {
    const newNodes = [...formData.nodes];
    const endNodeIndex = newNodes.findIndex(node => node.node_type === 'end');
    
    newNodes.splice(endNodeIndex, 0, {
      node_type: 'approve',
      configuration: { required_role: '', step_name: '' }
    });
    
    setFormData({ ...formData, nodes: newNodes });
  };

  const removeApprovalStep = (index) => {
    const newNodes = formData.nodes.filter((_, i) => i !== index);
    setFormData({ ...formData, nodes: newNodes });
  };

  const updateNodeConfig = (index, field, value) => {
    const newNodes = [...formData.nodes];
    newNodes[index].configuration[field] = value;
    setFormData({ ...formData, nodes: newNodes });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingWorkflow ? `/workflow_templates/${editingWorkflow.id}` : '/workflow_templates';
      const method = editingWorkflow ? 'patch' : 'post';
      
      const response = await api[method](url, {
        workflow_template: {
          name: formData.name,
          template_data: formData.template_data,
          department_id: formData.department_id || null,
          army_type_id: formData.army_type_id || null,
          workflow_type: formData.workflow_type,
          priority: formData.priority
        },
        nodes: formData.nodes
      });
      
      if (response.response.ok) {
        alert(`Workflow ${editingWorkflow ? 'updated' : 'created'} successfully`);
        setShowForm(false);
        setEditingWorkflow(null);
        fetchWorkflows();
        resetForm();
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      alert('Failed to save workflow');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      template_data: '',
      department_id: '',
      army_type_id: '',
      workflow_type: 'leave',
      priority: 0,
      nodes: [
        { node_type: 'start', configuration: {} },
        { node_type: 'approve', configuration: { required_role: '', step_name: '' } },
        { node_type: 'end', configuration: {} }
      ]
    });
    setEditingWorkflow(null);
  };

  const getNodeTypeLabel = (nodeType) => {
    const labels = {
      start: t('start'),
      approve: t('approval'),
      condition: t('condition'),
      notify: t('notification'),
      end: t('end')
    };
    return labels[nodeType] || nodeType;
  };

  const handleFilterChange = (type) => {
    console.log('Filter Type:', type);
    setFilterType(type);
    fetchWorkflows(type);
  };

  return (
    <>
      {/* Filter Controls */}
      <div className="mb_20 baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <h2 className="txt20 fwMedium colorBlack">Filter Workflows</h2>
        </div>
        <div style={{ padding: '0px 20px 20px 20px' }}>
          <div className="formInpt">
            <label className="mb_10">Workflow Type</label>
            <select
              value={filterType}
              className="customSelect"
              onChange={(e) => handleFilterChange(e.target.value)}
            >
              <option value="">All Types</option>
              {workflowTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Create/Edit Workflow Form */}
      {showForm && (
        <div className="mb_40 baseTableWrap">
          <div className="dFlex baseTableHd mb_16">
            <h2 className="txt20 fwMedium colorBlack">
              {editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}
            </h2>
          </div>
          <form onSubmit={handleSubmit} style={{ padding: '0px 20px 7px 20px' }}>
            <div className="row">
              <div className="col-md-6">
                <div className="formInpt mb_20">
                  <label className="mb_10">Workflow Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Leave Approval - Infantry Department"
                    required
                  />
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="formInpt mb_20">
                  <label className="mb_10">Workflow Type *</label>
                  <select
                    value={formData.workflow_type}
                    className="customSelect"
                    onChange={(e) => setFormData({...formData, workflow_type: e.target.value})}
                    required
                  >
                    {workflowTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div className="formInpt mb_20">
                  <label className="mb_10">Army Type (Optional)</label>
                  <select
                    value={formData.army_type_id}
                    className="customSelect"
                    onChange={(e) => setFormData({...formData, army_type_id: e.target.value})}
                  >
                    <option value="">All Army Types</option>
                    {armyTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="formInpt mb_20">
                  <label className="mb_10">Department (Optional)</label>
                  <select
                    value={formData.department_id}
                    className="customSelect"
                    onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                  >
                    <option value="">All Departments</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-md-4">
                <div className="formInpt mb_20">
                  <label className="mb_10">Priority</label>
                  <select
                    value={formData.priority}
                    className="customSelect"
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                  >
                    <option value={0}>Normal</option>
                    <option value={1}>High</option>
                    <option value={2}>Critical</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="formInpt mb_20">
              <label className="mb_10">Description</label>
              <textarea
                className="form-control"
                value={formData.template_data}
                onChange={(e) => setFormData({...formData, template_data: e.target.value})}
                placeholder="Describe this workflow..."
                rows="3"
              />
            </div>

            {/* Workflow Steps - Keep existing implementation */}
            <div className="mt_24">
              <div className="dFlex mb_16 justify-content-between">
                <h3 className="txt16 fwMedium">Workflow Steps</h3>
                <button 
                  type="button" 
                  className="btn btnSecondary"
                  onClick={() => {
                    const newNodes = [...formData.nodes];
                    const endNodeIndex = newNodes.findIndex(node => node.node_type === 'end');
                    newNodes.splice(endNodeIndex, 0, {
                      node_type: 'approve',
                      configuration: { required_role: '', step_name: '' }
                    });
                    setFormData({ ...formData, nodes: newNodes });
                  }}
                >
                  Add Approval Step
                </button>
              </div>

              <div className="workflowSteps">
                {formData.nodes.map((node, index) => (
                  <div key={index} className="workflowStep mb_16" style={{
                    padding: '16px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <div className="dFlex mb_10" style={{alignItems: 'center', gap: '10px'}}>
                      <span className="stepNumber" style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px'
                      }}>
                        {index + 1}
                      </span>
                      <strong>{node.node_type === 'start' ? 'Start' : node.node_type === 'end' ? 'End' : 'Approval'}</strong>
                      {node.node_type === 'approve' && index > 0 && index < formData.nodes.length - 1 && (
                        <button 
                          type="button"
                          className="btn btnSmall"
                          onClick={() => {
                            const newNodes = formData.nodes.filter((_, i) => i !== index);
                            setFormData({ ...formData, nodes: newNodes });
                          }}
                          style={{marginLeft: 'auto'}}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {node.node_type === 'approve' && (
                      <div className="row">
                        <div className="col-md-6">
                          <div className="formInpt">
                            <label className="mb_10">Step Name</label>
                            <input
                              type="text"
                              value={node.configuration.step_name || ''}
                              onChange={(e) => {
                                const newNodes = [...formData.nodes];
                                newNodes[index].configuration.step_name = e.target.value;
                                setFormData({ ...formData, nodes: newNodes });
                              }}
                              placeholder="e.g., Supervisor Approval"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="formInpt">
                            <label className="mb_10">Required Role *</label>
                            <select
                              value={node.configuration.required_role || ''}
                              onChange={(e) => {
                                const newNodes = [...formData.nodes];
                                newNodes[index].configuration.required_role = e.target.value;
                                setFormData({ ...formData, nodes: newNodes });
                              }}
                              required
                            >
                              <option value="">Select Role</option>
                              {roles.map(role => (
                                <option key={role.id} value={role.name}>{role.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="btnSet mt_24">
              <button type="submit" className="btn btnPrimary">
                {editingWorkflow ? 'Update Workflow' : 'Create Workflow'}
              </button>
              <button 
                type="button" 
                className="btn btnSecondary"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Workflows Table */}
      <div className="mb_40 baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <h2 className="txt20 fwMedium colorBlack">Workflow Templates</h2>
          <div className="dFlex baseTableTR">
            <button className="btnAdd me-3" onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}>
              {showForm ? 'Cancel' : 'Create Workflow'}
            </button>
          </div>
        </div>
        <div className="baseTableContainer">
          <table className="baseTable">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Army Type</th>
                <th>Department</th>
                <th>Steps</th>
                <th>Priority</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {workflows.length > 0 ? (
                workflows.map(workflow => (
                  <tr key={workflow.id}>
                    <td>{workflow.name}</td>
                    <td>
                      <span className="badge bg-primary">
                        {workflowTypes.find(t => t.value === workflow.workflow_type)?.label || workflow.workflow_type}
                      </span>
                    </td>
                    <td>{workflow.army_type?.name || 'All'}</td>
                    <td>{workflow.department?.name || 'All'}</td>
                    <td>{workflow.workflow_nodes?.length || 0} steps</td>
                    <td>
                      <span className={`badge ${workflow.priority === 2 ? 'bg-danger' : workflow.priority === 1 ? 'bg-warning' : 'bg-secondary'}`}>
                        {workflow.priority === 2 ? 'Critical' : workflow.priority === 1 ? 'High' : 'Normal'}
                      </span>
                    </td>
                    <td>
                      <div className="dFlex optBlock">
                        <button onClick={() => handleView(workflow.id)} className="optBtn">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-eye" viewBox="0 0 16 16">
                            <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z"/>
                            <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0"/>
                          </svg>
                          <span>View</span>
                        </button>
                        <span className="bar">|</span>
                        <button className="optBtn" onClick={() => handleEdit(workflow.id)}>
                          <img src="/images/edit.png" alt="" />
                          <span>Edit</span>
                        </button>
                        <span className="bar">|</span>
                        <button className="optBtn" onClick={() => handleDelete(workflow.id)}>
                          <img src="/images/delete.png" alt="" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{textAlign: 'center', padding: '20px'}}>
                    No workflows found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Workflow Modal */}
      <CommonModal
        isOpen={!!viewingWorkflow}
        onClose={() => setViewingWorkflow(null)}
        title="Workflow Details"
        size="lg"
      >
        {viewingWorkflow && (
          <div className="workflow-details">
            <p><strong>Name:</strong> {viewingWorkflow.name}</p>
            <p><strong>Type:</strong> {workflowTypes.find(t => t.value === viewingWorkflow.workflow_type)?.label}</p>
            <p><strong>Army Type:</strong> {viewingWorkflow.army_type?.name || 'All'}</p>
            <p><strong>Department:</strong> {viewingWorkflow.department?.name || 'All'}</p>
            <p><strong>Priority:</strong> {viewingWorkflow.priority === 2 ? 'Critical' : viewingWorkflow.priority === 1 ? 'High' : 'Normal'}</p>
            <p><strong>Description:</strong> {viewingWorkflow.template_data || 'No description'}</p>

            <h3 className="txt16 fwMedium mt_20 mb_10">Workflow Steps:</h3>
            {viewingWorkflow.workflow_nodes?.map((node, index) => (
              <div key={index} className="mb_10" style={{padding: '10px', border: '1px solid #ddd', borderRadius: '4px'}}>
                <strong>Step {index + 1}: {node.node_type === 'start' ? 'Start' : node.node_type === 'end' ? 'End' : 'Approval'}</strong>
                {node.node_type === 'approve' && (
                  <div>
                    <p>Step Name: {JSON.parse(node.configuration || '{}').step_name || 'N/A'}</p>
                    <p>Required Role: {JSON.parse(node.configuration || '{}').required_role || 'N/A'}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CommonModal>
    </>
  );
};

export default WorkflowManagement;